import React, { useEffect, useState } from 'react';
import { EventBus, EVENTS } from '../../../shared/EventBus';
import { PLAYER_UI } from '../theme';

/** How long the veil takes to lift once the destination is ready, ms. */
export const TRAVEL_VEIL_FADE_MS = 220;

/**
 * TravelVeil — covers the world during an in-place portal trip (plan 25).
 * The old map is torn down before the new one is built; without a cover the
 * player sees an empty stage for that moment. Drops at once on travel start,
 * lifts with a short fade when the destination scene is ready. Renders
 * nothing outside a trip.
 */
export const TravelVeil: React.FC = () => {
  const [phase, setPhase] = useState<'idle' | 'covering' | 'lifting'>('idle');

  useEffect(() => {
    const bus = EventBus.getInstance();
    let liftTimer: ReturnType<typeof setTimeout> | null = null;
    const offStart = bus.on(EVENTS.MAP_TRAVEL_START, () => {
      if (liftTimer) clearTimeout(liftTimer);
      liftTimer = null;
      setPhase('covering');
    });
    const offComplete = bus.on(EVENTS.MAP_TRAVEL_COMPLETE, (data) => {
      // A failed trip reloads the page; keep the cover up until it goes.
      if (!data.ok) return;
      setPhase('lifting');
      liftTimer = setTimeout(() => setPhase('idle'), TRAVEL_VEIL_FADE_MS);
    });
    return () => {
      offStart();
      offComplete();
      if (liftTimer) clearTimeout(liftTimer);
    };
  }, []);

  if (phase === 'idle') return null;
  return (
    <div
      data-testid="travel-veil"
      aria-hidden="true"
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 9998,
        pointerEvents: 'none',
        background: PLAYER_UI.inkDeep,
        opacity: phase === 'covering' ? 1 : 0,
        transition: phase === 'lifting' ? `opacity ${TRAVEL_VEIL_FADE_MS}ms ease-out` : 'none',
      }}
    />
  );
};
